import styled from 'styled-components';
import React, { useContext, useMemo } from 'react';
import { ArrowContainer, Popover } from 'react-tiny-popover';
import { GeneralText } from '@/features/editor/widgets/shared/general-text';
import { EditorContext } from '@/features/editor/context/editor-context';
import {
  EditorChangeKind,
  ExperienceApprovalStatus,
  PlacementKind,
  TargetingDeviceType,
} from '@/webapi/use-experience-api';
import { useTranspiler } from '@/features/editor/context/use-transpiler';
import {
  outsideButNotReactSelect,
  PreviewPopoverContentNew,
} from '@/features/editor/widgets/shared/preview-popover-new';
import { PopConfirm } from '@/components/pop-confirm';
import { VSpace } from '@/components/spacing';

export function DeveloperZone() {
  const {
    experienceControls,
    experienceState: {
      currentExperience,
      findEditorChange,
      setApprovalStatus,
      setPriceTestJavascriptPerVariant,
    },
    inspectorNav: { gotoCompoundEditor },
    resources: { control },
  } = useContext(EditorContext);

  const { globalJsId, asSingleValueBlock } = useTranspiler();

  const [previewVisible, setPreviewVisible] = React.useState(false);

  const togglePopover = () => {
    setPreviewVisible((prev) => !prev);
  };

  const getJsChange = () => {
    const editorId = globalJsId(currentExperience.id);
    return { change: findEditorChange(editorId), editorId };
  };

  const hasJsChange = useMemo(() => getJsChange(), [currentExperience]);

  const onJavascriptPerVariantClicked = () => {
    setPriceTestJavascriptPerVariant(!currentExperience.priceTestJsPerVariant);
  };

  const onJavascriptClicked = () => {
    // eslint-disable-next-line prefer-const
    let { change, editorId } = getJsChange();
    if (!change) {
      change = asSingleValueBlock(
        EditorChangeKind.GLOBAL_JS,
        editorId,
        `appendJs`,
        ``,
        `div`,
      );
      change.targetDevice = TargetingDeviceType.ALL_DEVICES;
      change.targetPlacement = { kind: PlacementKind.ALL_PAGES };
    }

    gotoCompoundEditor(change, false, true);
  };

  const onPreview = async (url: string, variant?: string) =>
    experienceControls.preview(url, variant);

  const onSharePreview = async (
    email: string,
    sharedBy: string,
    quickPreviewUrl?: string,
  ) => experienceControls.sharePreview(email, sharedBy, quickPreviewUrl);

  const onStatusInitial = async () => {
    setApprovalStatus(ExperienceApprovalStatus.NONE_APPROVAL);
  };

  const onStatusPending = async () => {
    setApprovalStatus(ExperienceApprovalStatus.PENDING_APPROVAL);
  };

  const onStatusApproved = async () => {
    setApprovalStatus(ExperienceApprovalStatus.APPROVED_APPROVAL);
  };

  const onStatusNotApproved = async () => {
    setApprovalStatus(ExperienceApprovalStatus.NOT_APPROVED_APPROVAL);
  };

  return (
    <Wrapper>
      <GeneralText>5. Configure the technicals (System users)</GeneralText>
      {!currentExperience.priceTestJsPerVariant && (
        <DeveloperButton onClick={onJavascriptClicked}>
          {hasJsChange ? `Edit` : `Add`} Global Javascript
        </DeveloperButton>
      )}
      <DeveloperButton onClick={onJavascriptPerVariantClicked}>
        {currentExperience?.priceTestJsPerVariant ? `Disable` : `Enable`} script
        per variant
      </DeveloperButton>

      <Popover
        isOpen={previewVisible}
        positions={[`top`]}
        onClickOutside={outsideButNotReactSelect(
          togglePopover,
          `exp-preview-content`,
        )}
        containerStyle={{ zIndex: `9999` }}
        content={({ position, childRect, popoverRect }) => (
          <ArrowContainer
            position={position}
            childRect={childRect}
            popoverRect={popoverRect}
            arrowColor="white"
            arrowSize={20}
            arrowStyle={{ opacity: 0.5 }}
            className="popover-arrow-container"
            arrowClassName="popover-arrow"
          >
            <PreviewPopoverContentNew
              onPreview={onPreview}
              onSharePreview={onSharePreview}
              quickPreviewUrl={currentExperience?.quickPreviewUrl}
              togglePopover={togglePopover}
              variants={[
                ...currentExperience.variants,
                ...(control ? [control] : []),
              ]}
              resetThemeToMain={currentExperience?.isThemeTest}
            />
          </ArrowContainer>
        )}
      >
        <DeveloperButton onClick={togglePopover}>Preview</DeveloperButton>
      </Popover>

      <VSpace value={2} />

      <GeneralText>
        6. Transition the experience status: (System users)
      </GeneralText>
      <div className="status-list">
        <DeveloperButton
          className={
            !currentExperience?.approvalStatus ||
            currentExperience?.approvalStatus ===
              ExperienceApprovalStatus.NONE_APPROVAL
              ? `active`
              : ``
          }
        >
          <PopConfirm
            text="Are you sure you want to set the status to Initial?"
            onConfirm={onStatusInitial}
          >
            Initial
          </PopConfirm>
        </DeveloperButton>

        <DeveloperButton
          className={
            currentExperience?.approvalStatus ===
            ExperienceApprovalStatus.PENDING_APPROVAL
              ? `active`
              : ``
          }
        >
          <PopConfirm
            text="Are you sure you want to set the status to Pending?"
            onConfirm={onStatusPending}
          >
            Pending
          </PopConfirm>
        </DeveloperButton>

        <DeveloperButton
          className={
            currentExperience?.approvalStatus ===
            ExperienceApprovalStatus.APPROVED_APPROVAL
              ? `active`
              : ``
          }
        >
          <PopConfirm
            text="Are you sure you want to set the status to Approved?"
            onConfirm={onStatusApproved}
          >
            Approved
          </PopConfirm>
        </DeveloperButton>

        <DeveloperButton
          className={
            currentExperience?.approvalStatus ===
            ExperienceApprovalStatus.NOT_APPROVED_APPROVAL
              ? `active`
              : ``
          }
        >
          <PopConfirm
            text="Are you sure you want to set the status to NotApproved?"
            onConfirm={onStatusNotApproved}
          >
            Not Approved
          </PopConfirm>
        </DeveloperButton>
      </div>
    </Wrapper>
  );
}

const Wrapper = styled.div`
  display: flex;
  flex-direction: column;
  align-items: flex-start;
  gap: 1.5rem;

  .status-list {
    display: flex;
    flex-direction: row;
    gap: 2rem;
  }
`;

const DeveloperButton = styled.button`
  background: transparent;
  border: none;
  color: #007bff;
  cursor: pointer;
  font-size: 1.4rem;
  text-decoration: underline;

  &:hover {
    text-decoration: none;
  }

  &.active {
    color: #656565;
    text-decoration: none;
    pointer-events: none;
  }
`;
